import { useContext, useState } from 'react';

import { useDebouncedCallback } from 'use-debounce'

import { DataManagerSearch } from '@webapp/components/DataManager/components/DataManagerSearch'
import { usePagination } from '@webapp/core/hooks/usePagination'
import { type PaginationID } from '@webapp/core/providers/Pagination/pagination.types'
import { PaginationControlsContext } from '@webapp/core/providers/Pagination/PaginationControlsContext'

export interface IPaginationSearchProps {
  id?: PaginationID
  autoFocus?: boolean
  placeholder?: string
  manualTrigger?: boolean
}

export const PaginationSearch = ({
  id: idOverride,
  manualTrigger = false,
  autoFocus = false,
  placeholder,
}: IPaginationSearchProps) => {
  const { id } = useContext(PaginationControlsContext)

  const paginationId = idOverride || id

  if (!paginationId) {
    throw new Error(
      'PaginationSearch must be used within a PaginationControls context or provide an id prop'
    )
  }

  const { params, updateParams } = usePagination({ id: paginationId })

  const [searchValue, setSearchValue] = useState(params.search)

  const updateManagerSearch = (searchTerm: string) => {
    updateParams({
      search: searchTerm,
      page: 1,
    })
  }

  const [showManualTriggerHelperText, setShowManualTriggerHelperText] =
    useState(false)

  const onChangeDebounced = useDebouncedCallback(updateManagerSearch, 250, {
    leading: false,
    trailing: true,
  })

  const onChangeTrottled = useDebouncedCallback(updateManagerSearch, 250, {
    leading: true,
    trailing: false,
  })

  const onInputChange = (searchTerm: string) => {
    setSearchValue(searchTerm)

    if (manualTrigger) {
      if (!searchTerm) {
        /**
         * Even when it is in manual trigger mode, we have to fire onChange when
         * the user is clearing the input either by emptying the input or pressing
         * the clear input button
         */
        setShowManualTriggerHelperText(false)
        onChangeDebounced(searchTerm)
      } else {
        setShowManualTriggerHelperText(true)
      }

      return
    }

    onChangeDebounced(searchTerm)
  }

  const onManualSearch = (searchTerm: string) => {
    setShowManualTriggerHelperText(false)

    onChangeTrottled(searchTerm)
  }

  return (
    <DataManagerSearch
      value={searchValue || ''}
      onChange={onInputChange}
      onSearch={onManualSearch}
      showManualSearchText={showManualTriggerHelperText}
      autoFocus={autoFocus}
      placeholder={placeholder}
    />
  )
}
